
import { useState } from 'react';
import { Navbar } from '../../components/feature/Navbar';
import { Footer } from '../../components/feature/Footer';
import { Link } from 'react-router-dom';
import { useSEO } from '../../utils/seo';

const galleryItems = [
  {
    beforeImage:
      'https://static.readdy.ai/image/29c6e5b725f148577e131a2b91b65f20/f6944ac5626a1653f3a3ef9224913597.jpeg',
    afterImage:
      'https://static.readdy.ai/image/29c6e5b725f148577e131a2b91b65f20/2e33cd4bf0fbc83b830d1cbb190e17ea.jpeg',
    title: 'Shed Renovation',
    category: 'Exterior',
    description:
      'Complete shed transformation with new siding and paint in Mobile AL',
  },
  {
    beforeImage:
      'https://static.readdy.ai/image/29c6e5b725f148577e131a2b91b65f20/9eb91a9c055cf9388ed3c866ddf6e0a7.jpeg',
    afterImage:
      'https://static.readdy.ai/image/29c6e5b725f148577e131a2b91b65f20/e61f5bbc2e3817d4c77af5089f874a84.jpeg',
    title: 'Brick Mailbox Build',
    category: 'Exterior',
    description:
      'Custom brick mailbox construction with arched top design in Baldwin County',
  },
  {
    beforeImage:
      'https://static.readdy.ai/image/29c6e5b725f148577e131a2b91b65f20/ee38bc37534bbe442cca1fef577f96a1.jpeg',
    afterImage:
      'https://static.readdy.ai/image/29c6e5b725f148577e131a2b91b65f20/45c4a22fb72ab1cc39569a797140df72.jpeg',
    title: 'Interior Remodel',
    category: 'Interior',
    description:
      'Drywall installation and interior renovation project in Mobile County',
  },
  {
    beforeImage:
      'https://static.readdy.ai/image/29c6e5b725f148577e131a2b91b65f20/3b876ef70f47b0f70b8d2d35e65e054d.jpeg',
    afterImage:
      'https://static.readdy.ai/image/29c6e5b725f148577e131a2b91b65f20/e5d8361f1007127c47910b74296cb9d9.jpeg',
    title: 'Exterior Renovation',
    category: 'Exterior',
    description:
      'Complete exterior makeover with new siding in Baldwin County AL',
  },
];

export default function GalleryPage() {
  useSEO({
    title:
      'Before & After Gallery | Home Remodeling Projects Mobile & Baldwin County AL',
    description:
      'View before and after photos of home improvement projects by S&L Contractors in Mobile & Baldwin Counties AL. Shed renovations, interior remodels, exterior makeovers, and custom builds. Call 251-348-8514.',
    keywords:
      'before after gallery, home remodeling projects Mobile AL, kitchen remodel Baldwin County, exterior renovation Alabama, contractor portfolio Mobile County',
    canonical: '/gallery',
    ogType: 'website',
    schemas: [
      {
        '@context': 'https://schema.org',
        '@type': 'ImageGallery',
        name: 'S&L Contractors Before & After Gallery',
        description:
          'Before and after photos of home improvement projects in Baldwin & Mobile Counties, Alabama',
        url: 'https://www.slcontractorsofal.com/gallery',
        provider: {
          '@type': 'GeneralContractor',
          '@id': 'https://www.slcontractorsofal.com/#organization',
          name: 'S&L Contractors of Alabama, Inc.',
          telephone: '+1-251-348-8514',
        },
        image: galleryItems.map((item) => ({
          '@type': 'ImageObject',
          name: item.title,
          description: item.description,
          contentUrl: item.afterImage,
        })),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: 'https://www.slcontractorsofal.com/',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Gallery',
            item: 'https://www.slcontractorsofal.com/gallery',
          },
        ],
      },
    ],
  });

  const [selectedImage, setSelectedImage] = useState<{
    image: string;
    title: string;
    type: 'before' | 'after';
  } | null>(null);

  const handleImageClick = (
    image: string,
    title: string,
    type: 'before' | 'after'
  ) => {
    setSelectedImage({ image, title, type });
  };

  return (
    <div className="min-h-screen bg-[#f5f0e6]">
      <Navbar />

      {/* Breadcrumb */}
      <nav
        className="pt-24 pb-2 px-4 max-w-7xl mx-auto"
        aria-label="Breadcrumb"
      >
        <ol className="flex items-center text-sm text-gray-500 space-x-2">
          <li>
            <a href="/" className="hover:text-sky-600 cursor-pointer">
              Home
            </a>
          </li>
          <li>
            <span>/</span>
          </li>
          <li className="text-gray-900 font-medium">Gallery</li>
        </ol>
      </nav>

      {/* Hero Section */}
      <header className="pt-4 pb-16 px-4 bg-gradient-to-br from-[#ebe5d9] to-[#f5f0e6]">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Before &amp; After Home Improvement Gallery
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            See the transformations we&apos;ve created for homeowners across{' '}
            <strong>Baldwin &amp; Mobile Counties</strong>. Each project
            showcases our commitment to quality{' '}
            <strong>home improvement</strong> craftsmanship and attention to
            detail.
          </p>
        </div>
      </header>

      {/* Gallery Grid */}
      <main className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="space-y-12">
            {galleryItems.map((item, index) => (
              <article key={index} className="space-y-4">
                <div className="text-center">
                  <span className="text-sky-500 text-sm font-medium uppercase tracking-wider">
                    {item.category}
                  </span>
                  <h3 className="text-gray-900 text-2xl font-bold mt-2">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 mt-1">{item.description}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div
                    onClick={() =>
                      handleImageClick(item.beforeImage, item.title, 'before')
                    }
                    className="group relative overflow-hidden rounded-lg cursor-pointer bg-white shadow-md hover:shadow-xl transition-all duration-300"
                  >
                    <div className="relative w-full h-80">
                      <img
                        src={item.beforeImage}
                        alt={`${item.title} before home improvement by S&L Contractors Mobile AL`}
                        title={`${item.title} - Before - S&L Contractors`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300"></div>
                    </div>
                    <div className="absolute top-4 left-4 bg-red-500 text-white text-sm font-bold px-3 py-1.5 rounded-md shadow-lg">
                      BEFORE
                    </div>
                  </div>

                  <div
                    onClick={() =>
                      handleImageClick(item.afterImage, item.title, 'after')
                    }
                    className="group relative overflow-hidden rounded-lg cursor-pointer bg-white shadow-md hover:shadow-xl transition-all duration-300"
                  >
                    <div className="relative w-full h-80">
                      <img
                        src={item.afterImage}
                        alt={`${item.title} after remodeling by S&L Contractors Baldwin County AL`}
                        title={`${item.title} - After - S&L Contractors`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300"></div>
                    </div>
                    <div className="absolute top-4 left-4 bg-green-500 text-white text-sm font-bold px-3 py-1.5 rounded-md shadow-lg">
                      AFTER
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </main>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            className="absolute top-6 right-6 w-12 h-12 flex items-center justify-center text-white hover:text-gray-300 cursor-pointer z-10"
            onClick={() => setSelectedImage(null)}
            aria-label="Close image lightbox"
          >
            <i className="ri-close-line text-3xl"></i>
          </button>
          <div className="max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="relative w-full h-[600px] rounded-lg overflow-hidden bg-white">
              <img
                src={selectedImage.image}
                alt={`${selectedImage.title} - ${selectedImage.type} - S&L Contractors home improvement project`}
                title={`${selectedImage.title} ${selectedImage.type} photo`}
                className="w-full h-full object-contain"
              />
            </div>
            <div className="mt-4 text-center">
              <div
                className={`inline-block px-4 py-2 rounded-md font-bold mb-2 ${
                  selectedImage.type === 'before' ? 'bg-red-500' : 'bg-green-500'
                } text-white`}
              >
                {selectedImage.type.toUpperCase()}
              </div>
              <h3 className="text-white text-2xl font-bold mt-2">
                {selectedImage.title}
              </h3>
            </div>
          </div>
        </div>
      )}

      {/* CTA Section */}
      <section className="py-20 px-4 bg-[#ebe5d9]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Like What You See?
          </h2>
          <p className="text-gray-600 text-lg mb-8">
            Let us help transform your home into the space you&apos;ve always
            dreamed of. Get a free estimate for your{' '}
            <strong>home improvement</strong> project in{' '}
            <strong>Mobile &amp; Baldwin Counties</strong>.
          </p>
          <a
            href="/contact"
            className="inline-block text-white px-8 py-4 rounded-md font-semibold transition-all whitespace-nowrap cursor-pointer"
            style={{ backgroundColor: '#1e3a5f' }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = '#152a45')
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = '#1e3a5f')
            }
          >
            Request a Free Quote
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
